import React, { ReactNode, FunctionComponent, useContext } from "react";

// MUI
import { Badge, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";

// Custom components
import UserContext from "@/components/UserContext/UserContext";
import SidebarContext, { SidebarContextType } from "./SidebarContext/SidebarContext";

// Types
import { Permission } from "@/@types/common";
import { UserContextType } from "@/@types/context/UserContext";

export interface ISidebarTabListItem {
  id: string
  icon: ReactNode
  disabled?: boolean
  title: string,
  onChange?: (id: string) => void
  needPermission?: Array<Permission>
  ref?: React.RefObject<HTMLAnchorElement>
  badgeContent?: number
  onSidebarCollapse?: ( ) => void;
  dontChangeUrl?: boolean;
}

const StyledLi = styled('li')(({theme}) => ({
  width: "100%",
  height: 40,
  color: "#FFF",
  fontSize: "12pt",
  overflow: "hidden",
  transition: "all 80ms",
  ":hover" : {
    color: "#FFF",
    backgroundColor: theme.palette.primary.dark
  }
}));

const DisabledLi = styled(StyledLi)(() => ({
  color: "rgba(255, 255, 255, 0.4)",
  ":hover": {
    backgroundColor: "transparent"
  }
}));

const ActiveLi = styled(StyledLi)(({theme}) => ({
  backgroundColor: theme.palette.primary.main
}));

const StyledA = styled('a')(() => ({
  display: "block",
  width: "100%",
  height: "100%",
  lineHeight: "40px",
  color: "inherit",
  textDecoration: "none",
  textAlign: "center"
}))

const DisabledA = styled(StyledA)(() => ({
  cursor: "default"
}))

const SidebarTabListItem: FunctionComponent<ISidebarTabListItem> = (props) => {
  const userContext = useContext(UserContext) as UserContextType;

  const { id, icon, disabled, title, onChange, needPermission, ref, badgeContent, onSidebarCollapse, dontChangeUrl } = props;

  const sidebarContext = useContext(SidebarContext) as SidebarContextType;
  // const userContext = useContext(UserContext);

  const handlePaneChange = (paneId: string): void => {
    if (!disabled) {
      // userContext.setUserSetting({map_sidebar_pane: id});
      sidebarContext.setActivePane(paneId);
      sidebarContext.setSidebarHeading(title);
      if (onSidebarCollapse) {
        sidebarContext.setOnSidebarCollapse(onSidebarCollapse);
      }

      if (onChange) {
        onChange(paneId);
      }
    }
  };

  const isActive = sidebarContext.activePane === id;

  const classNames = [];
  if (disabled) {
    classNames.push("disabled");
  }
  if (isActive) {
    classNames.push("active");
  }
  const joinedClassNames = classNames.length > 0 ? classNames.join(" ") : "";

  const LI = disabled ? DisabledLi : isActive ? ActiveLi : StyledLi;
  const A = disabled ? DisabledA : StyledA;

  return needPermission && !userContext.hasAnyPermission(needPermission) ? null : (
    <LI className={joinedClassNames} title={title} >
      <A href={dontChangeUrl ? undefined :"#" + id} role="tab" ref={ref} onClick={() => handlePaneChange(id)}>
        <Badge color="secondary" badgeContent={badgeContent}>
          {icon}
        </Badge>
      </A>
    </LI>
  );
};

export default SidebarTabListItem;
